import { BusinessFormFields, LegalStructure } from '@/types/businesses';
import {
  SVCheckbox,
  SVForm,
  SVLabeledText,
  SVAutocomplete,
  SVSSNInput,
  SVTextInput,
} from '../core';
import { Card, Grid, Stack, Typography } from '@mui/material';
import { FORM_FIELDS } from '@/constants/formFields';
import { useGetCompanyListForSelection } from '@/hooks/requests/useCompanies';
import { useGetTeammateListForSelection } from '@/hooks/requests/useTeammates';
import AddressFormFields from '../AddressFormFields';
import BusinessMembersForm from './BusinessMembersForm';
import FormSubmissionButtons from '../FormSubmissionButtons';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import { TeammateRole } from '@/types/teammates';
import { useEffect, useState } from 'react';
import { blankBusinessMember, initialBusinessForm } from '@/constants/business';
import { MESSAGES } from '@/constants/messages';
import {
  useAddBusiness,
  useEditBusiness,
} from '@/hooks/requests/useBusinesses';
import { useGetUser } from '@/hooks/requests/useUser';
import showToast from '@/utils/toast';
import { useFieldArray, useForm } from 'react-hook-form';
import BusinessAddCustomerForm from './BusinessAddCustomerForm';
import { validateBusinessMemberForm } from '@/utils/business';

type BusinessFormProps = {
  disableCompanyAndAdvisor?: boolean;
  isEdit?: boolean;
  defaultValues?: BusinessFormFields;
  onLeave?: () => void;
  onSuccess: (uuid: string) => void;
};

export default function BusinessForm({
  disableCompanyAndAdvisor = false,
  isEdit,
  defaultValues,
  onLeave,
  onSuccess,
}: BusinessFormProps) {
  const [openAddCustomerModal, setOpenAddCustomerModal] = useState(false);

  const { isStaff } = useAuthUser();
  const { user } = useGetUser();
  const { addBusiness, isAddBusinessPending } = useAddBusiness();
  const { editBusiness, isEditBusinessPending } = useEditBusiness();

  const formMethods = useForm<BusinessFormFields>({
    defaultValues: defaultValues
      ? defaultValues
      : {
          ...initialBusinessForm,
          company: !isStaff ? user?.teammateInfo?.company.uuid : '',
        },
    mode: 'onBlur',
  });

  const membersFieldArray = useFieldArray<BusinessFormFields>({
    control: formMethods.control,
    name: 'members' as const,
  });

  async function handleSubmit(data: BusinessFormFields) {
    if (!isEdit && !validateBusinessMemberForm(data)) {
      return;
    }

    try {
      let response = '';
      if (isEdit) {
        response = await editBusiness(data);
        onSuccess(data.uuid);
      } else {
        response = await addBusiness(data);
        onSuccess(response);
      }

      showToast({
        type: 'success',
        data: {
          content: `Business has been successfully ${isEdit ? 'edited' : 'added'}`,
        },
      });
    } catch (error: any) {
      showToast({
        type: 'error',
        data: {
          content: error?.response?.data
            ? error.response.data
            : MESSAGES.genericError,
        },
      });
    }
  }
  const [company, advisors, sameMailingAddress, members] = formMethods.watch([
    'company',
    'advisors',
    'sameMailingAddress',
    'members',
  ]);

  useEffect(() => {
    formMethods.setValue('members', [blankBusinessMember]);
  }, [company]);

  const { companiesList } = useGetCompanyListForSelection({}, true);
  const { teammatesList, isTeammatesListPending } =
    useGetTeammateListForSelection(
      company
        ? {
            company_id: company,
            roles: [TeammateRole.ADMIN, TeammateRole.ADVISOR],
          }
        : { roles: [TeammateRole.ADMIN, TeammateRole.ADVISOR] },
    );
  const doesntHaveTeammates =
    Boolean(company) && teammatesList.length === 0 && !isTeammatesListPending;
  const showMembersForm =
    !isEdit &&
    company &&
    advisors.length > 0 &&
    !doesntHaveTeammates &&
    !isTeammatesListPending;

  return (
    <>
      <SVForm<BusinessFormFields> methods={formMethods} onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <Card>
            <Typography variant="h5" mb={2}>
              Basic Info
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <SVTextInput
                  name="name"
                  label="Legal Business Name"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <SVTextInput name="dbaName" label="DBA (optional)" />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <SVAutocomplete
                  name="legalStructure"
                  label="Legal Structure"
                  options={[
                    {
                      label: 'Corp',
                      value: LegalStructure.CORP.toString(),
                    },
                    {
                      label: 'Estate',
                      value: LegalStructure.ESTATE.toString(),
                    },
                    {
                      label: 'LLC',
                      value: LegalStructure.LLC.toString(),
                    },
                    {
                      label: 'LLP',
                      value: LegalStructure.LLP.toString(),
                    },
                    {
                      label: 'Nonprofit',
                      value: LegalStructure.NONPROFIT.toString(),
                    },
                    {
                      label: 'Sole Proprietorship',
                      value: LegalStructure.SOLEPROPRIETORSHIP.toString(),
                    },
                    {
                      label: 'Trust',
                      value: LegalStructure.TRUST.toString(),
                    },
                  ]}
                  rules={{ required: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVSSNInput name="tin" label="TIN" disabled={isEdit} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVSSNInput
                  name="confirmTin"
                  label="Confirm TIN"
                  matchingInputName="tin"
                  disabled={isEdit}
                />
              </Grid>
              {isStaff && (
                <Grid size={{ xs: 12, md: 6 }}>
                  {isEdit ? (
                    <SVLabeledText
                      label="Company"
                      text={defaultValues?.companyName || ''}
                    />
                  ) : (
                    <SVAutocomplete
                      name="company"
                      options={companiesList}
                      disabled={isEdit || disableCompanyAndAdvisor}
                      {...FORM_FIELDS.COMPANY}
                    />
                  )}
                </Grid>
              )}
              {doesntHaveTeammates ? (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography color="error">
                    Customers must be associated to a teammate and the current
                    selected company has no assigned teammates.
                  </Typography>
                </Grid>
              ) : (
                <Grid size={{ xs: 12, md: 6 }}>
                  {teammatesList.length ? (
                    <SVAutocomplete
                      name="advisors"
                      options={teammatesList}
                      multiple
                      disabled={!company || disableCompanyAndAdvisor}
                      {...FORM_FIELDS.ADVISORS}
                    />
                  ) : null}
                </Grid>
              )}
            </Grid>
          </Card>
          <Card>
            <Typography variant="h5" mb={2}>
              Current Address
            </Typography>
            <AddressFormFields
              line1Name="currentAddress.line1"
              line2Name="currentAddress.line2"
              cityName="currentAddress.city"
              stateName="currentAddress.state"
              postalCodeName="currentAddress.postalCode"
            />
          </Card>
          <Card>
            <Typography variant="h5" mb={2}>
              Mailing Address
            </Typography>
            <SVCheckbox
              name="sameMailingAddress"
              {...FORM_FIELDS.SAME_ADDRESS}
            />
            {!sameMailingAddress && (
              <AddressFormFields
                line1Name="mailingAddress.line1"
                line2Name="mailingAddress.line2"
                cityName="mailingAddress.city"
                stateName="mailingAddress.state"
                postalCodeName="mailingAddress.postalCode"
              />
            )}
          </Card>
          {showMembersForm ? (
            <Card>
              <Typography variant="h5" mb={2}>
                Members
              </Typography>
              <BusinessMembersForm
                companyId={company}
                advisorIds={advisors}
                setOpenAddCustomerModal={setOpenAddCustomerModal}
                fieldArray={membersFieldArray}
              />
            </Card>
          ) : null}
          {doesntHaveTeammates && (
            <Typography color="error">
              Customers must be associated to a teammate and the current
              selected company has no assigned teammates.
            </Typography>
          )}
          <FormSubmissionButtons
            isLoading={isAddBusinessPending || isEditBusinessPending}
            onLeave={onLeave}
            submitButtonProps={{
              disabled: doesntHaveTeammates,
            }}
          />
        </Stack>
      </SVForm>
      <BusinessAddCustomerForm
        formMethods={formMethods}
        open={openAddCustomerModal}
        setOpen={setOpenAddCustomerModal}
        company={company}
        advisors={advisors}
        members={members}
        fieldArray={membersFieldArray}
      />
    </>
  );
}
